import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { Menu, X } from "lucide-react";

const NAV = [
  { to: "/modules", key: "nav.modules" },
  { to: "/secteurs", key: "nav.sectors" },
  { to: "/tarifs", key: "nav.pricing" },
  { to: "/demo", key: "nav.demo" },
  { to: "/a-propos", key: "nav.about" },
  { to: "/blog", key: "nav.blog" },
  { to: "/faq", key: "nav.faq" },
  { to: "/contact", key: "nav.contact" },
];

function LangToggle({ compact = false }) {
  const { lang, setLang } = useLang();
  return (
    <div
      className={`flex items-center rounded-lg border border-slate-200 bg-white p-0.5 ${compact ? "w-max" : ""}`}
      data-testid="lang-toggle"
    >
      {["fr", "en"].map((l) => (
        <button
          key={l}
          onClick={() => setLang(l)}
          data-testid={`lang-${l}`}
          className={`rounded-md px-2.5 py-1 text-xs font-bold uppercase transition-colors ${
            lang === l ? "bg-marine text-white" : "text-slate-500 hover:text-marine"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

export function Header() {
  const { t } = useLang();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const close = () => setOpen(false);

  return (
    <header
      className="sticky top-0 z-50 border-b border-slate-200/70 bg-white/80 backdrop-blur-xl"
      data-testid="site-header"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6">
        <Link to="/" className="flex items-center gap-2.5" data-testid="header-logo" onClick={close}>
          <img src="/brand/icon.png" alt="MANAGONE" className="h-9 w-9 rounded-lg object-contain" />
          <span className="font-display text-lg font-extrabold tracking-tight text-marine">MANAGONE</span>
        </Link>

        <nav className="hidden items-center gap-1 xl:flex" data-testid="desktop-nav">
          {NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={`nav-link-${item.key.split(".")[1]}`}
              className={({ isActive }) =>
                `rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                  isActive ? "bg-brand/10 text-brand" : "text-slate-600 hover:bg-slate-100 hover:text-marine"
                }`
              }
            >
              {t(item.key)}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-3 xl:flex">
          <LangToggle />
          <Link to="/contact" className="btn-primary" data-testid="header-cta">
            {t("nav.cta")}
          </Link>
        </div>

        <div className="flex items-center gap-2 xl:hidden">
          <LangToggle compact />
          <button
            onClick={() => setOpen((v) => !v)}
            className="rounded-lg border border-slate-200 bg-white p-2 text-marine"
            aria-label="Menu"
            data-testid="mobile-menu-button"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="border-t border-slate-200 bg-white px-4 py-3 xl:hidden" data-testid="mobile-nav" key={location.pathname}>
          <div className="flex flex-col gap-1">
            {NAV.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                onClick={close}
                data-testid={`mobile-nav-${item.key.split(".")[1]}`}
                className={({ isActive }) =>
                  `rounded-lg px-3 py-2.5 text-sm font-medium ${
                    isActive ? "bg-brand/10 text-brand" : "text-slate-600 hover:bg-slate-100"
                  }`
                }
              >
                {t(item.key)}
              </NavLink>
            ))}
            <Link to="/contact" onClick={close} className="btn-primary mt-2" data-testid="mobile-cta">
              {t("nav.cta")}
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
}
